import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  Skeleton,
  Card,
  CardBody,
  Select,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Flex,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import {
  FiSearch,
  FiFilter,
  FiChevronLeft,
  FiChevronRight,
  FiCheckCircle,
  FiXCircle,
  FiClock,
} from 'react-icons/fi';
import { useGetSettlementPaymentHistory } from '../../../services/payrollService';

// Helper function to format currency with currency code
const formatCurrencyWithCode = (amount, currencyCode) => {
  if (amount === null || amount === undefined) return '0';
  
  const currencySymbols = {
    'USD': '$',
    'NGN': '₦',
    'GHS': '₵',
    'ZAR': 'R',
    'KES': 'KSh',
    'GBP': '£'
  };
  
  const symbol = currencySymbols[currencyCode] || '$';
  const numericAmount = parseFloat(amount) || 0;
  
  const formattedAmount = numericAmount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  return `${symbol}${formattedAmount}`;
};

// Format date
const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const SettlementPaymentHistory = () => {
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const cardBg = useColorModeValue("white", "gray.700");
  
  // Pagination and filters
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [categoryNameFilter, setCategoryNameFilter] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // API hook
  const {
    data: paymentHistoryData,
    isLoading: paymentHistoryLoading,
    error: paymentHistoryError,
    refetch: refetchPaymentHistory
  } = useGetSettlementPaymentHistory({
    page,
    limit,
    ...(categoryNameFilter && { category_name: categoryNameFilter }),
    ...(paymentStatusFilter && { payment_status: paymentStatusFilter }),
  });
  
  // Extract data - API returns payment_history directly
  const paymentHistory = paymentHistoryData?.data || paymentHistoryData;
  const payments = paymentHistory?.payment_history || paymentHistory?.payments || [];
  const total = paymentHistory?.total_count || paymentHistory?.pagination?.total || 0;
  const totalPages = Math.ceil(total / limit);
  const currentPage = paymentHistory?.page || page;
  
  // Get status color
  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'SUCCESS':
      case 'COMPLETED':
        return 'green';
      case 'FAILED':
        return 'red';
      case 'PENDING':
        return 'orange';
      case 'PROCESSING':
        return 'blue';
      default:
        return 'gray';
    }
  };
  
  // Get status icon
  const getStatusIcon = (status) => {
    switch (status?.toUpperCase()) {
      case 'SUCCESS':
      case 'COMPLETED':
        return <FiCheckCircle />;
      case 'FAILED':
        return <FiXCircle />;
      case 'PENDING':
        return <FiClock />;
      case 'PROCESSING':
        return <FiClock />;
      default:
        return null;
    }
  };
  
  // Get status text
  const getStatusText = (status) => {
    switch (status?.toUpperCase()) {
      case 'SUCCESS':
        return 'Success';
      case 'COMPLETED':
        return 'Completed';
      case 'FAILED':
        return 'Failed';
      case 'PENDING':
        return 'Pending';
      case 'PROCESSING':
        return 'Processing';
      default:
        return status || 'Unknown';
    }
  };
  
  // Filter payments by search term
  const filteredPayments = payments.filter(payment => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      payment.category_name?.toLowerCase().includes(search) ||
      payment.payment_reference?.toLowerCase().includes(search) ||
      payment.account_number_masked?.toLowerCase().includes(search) ||
      payment.account_number?.toLowerCase().includes(search) ||
      payment.flutterwave_transfer_id?.toLowerCase().includes(search) ||
      payment.direct_debit_transaction_id?.toLowerCase().includes(search) ||
      payment.payment_method?.toLowerCase().includes(search) ||
      payment.processed_by?.toLowerCase().includes(search) ||
      payment.bank_name?.toLowerCase().includes(search)
    );
  });
  
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setPage(currentPage - 1);
    }
  };
  
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setPage(currentPage + 1);
    }
  };
  
  const handleClearFilters = () => {
    setCategoryNameFilter('');
    setPaymentStatusFilter('');
    setSearchTerm('');
    setPage(1);
  };
  
  if (paymentHistoryLoading) {
    return (
      <VStack spacing={4} align="stretch">
        <Skeleton height="100px" borderRadius="lg" />
        <Skeleton height="400px" borderRadius="lg" />
      </VStack>
    );
  }
  
  if (paymentHistoryError) {
    return (
      <Alert status="error" borderRadius="lg">
        <AlertIcon />
        <AlertTitle>Failed to load payment history</AlertTitle>
        <AlertDescription>
          {paymentHistoryError?.message || "Unable to retrieve payment history. Please try again."}
        </AlertDescription>
      </Alert>
    );
  }
  
  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <HStack justify="space-between" align="center">
        <VStack align="start" spacing={1}>
          <Heading size="lg" color="gray.800">
            Payment History
          </Heading>
          <Text color="gray.600" fontSize="sm">
            View all settlement payment transactions
          </Text>
        </VStack>
      </HStack>
      
      {/* Filters */}
      <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <HStack spacing={4} flexWrap="wrap">
              <InputGroup flex="1" minW="250px">
                <InputLeftElement pointerEvents="none">
                  <FiSearch color="gray.300" />
                </InputLeftElement>
                <Input
                  placeholder="Search by category, reference, account..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
              
              <Select
                placeholder="Filter by Category"
                value={categoryNameFilter}
                onChange={(e) => {
                  setCategoryNameFilter(e.target.value);
                  setPage(1);
                }}
                maxW="200px"
              >
                <option value="">All Categories</option>
                {/* Add unique categories dynamically if available */}
              </Select>
              
              <Select
                placeholder="Filter by Status"
                value={paymentStatusFilter}
                onChange={(e) => {
                  setPaymentStatusFilter(e.target.value);
                  setPage(1);
                }}
                maxW="200px"
              >
                <option value="">All Statuses</option>
                <option value="SUCCESS">Success</option>
                <option value="COMPLETED">Completed</option>
                <option value="PENDING">Pending</option>
                <option value="PROCESSING">Processing</option>
                <option value="FAILED">Failed</option>
              </Select>
              
              {(categoryNameFilter || paymentStatusFilter || searchTerm) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearFilters}
                >
                  Clear Filters
                </Button>
              )}
            </HStack>
            
            {total > 0 && (
              <Text fontSize="sm" color="gray.600">
                Showing {((currentPage - 1) * limit) + 1} - {Math.min(currentPage * limit, total)} of {total} payments
              </Text>
            )}
          </VStack>
        </CardBody>
      </Card>
      
      {/* Payment History Table */}
      {filteredPayments.length > 0 ? (
        <Card bg={cardBg} border="1px solid" borderColor={borderColor} overflow="hidden">
          <TableContainer>
            <Table variant="simple">
              <Thead bg="gray.50">
                <Tr>
                  <Th>Date</Th>
                  <Th>Category</Th>
                  <Th>Category Type</Th>
                  <Th>Amount</Th>
                  <Th>Payment Method</Th>
                  <Th>Account</Th>
                  <Th>Transaction ID</Th>
                  <Th>Status</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredPayments.map((payment, index) => (
                  <Tr key={payment.id || index}>
                    <Td>
                      <VStack align="start" spacing={0}>
                        <Text fontSize="sm" color="gray.700" fontWeight="600">
                          {formatDate(payment.completed_at || payment.processed_at || payment.payment_date || payment.created_at)}
                        </Text>
                        {payment.processed_at && payment.completed_at && payment.processed_at !== payment.completed_at && (
                          <Text fontSize="xs" color="gray.500">
                            Processed: {formatDate(payment.processed_at)}
                          </Text>
                        )}
                      </VStack>
                    </Td>
                    <Td>
                      <Text fontWeight="600" color="gray.800">
                        {payment.category_name}
                      </Text>
                    </Td>
                    <Td>
                      <Badge 
                        colorScheme={payment.category_type === 'TAX' ? 'blue' : 'purple'}
                        variant="subtle"
                      >
                        {payment.category_type}
                      </Badge>
                    </Td>
                    <Td>
                      <Text fontWeight="600" color="gray.800">
                        {formatCurrencyWithCode(payment.amount || 0, payment.currency || 'NGN')}
                      </Text>
                    </Td>
                    <Td>
                      <Badge 
                        colorScheme={payment.payment_method === 'direct_debit' ? 'orange' : 'green'}
                        variant="subtle"
                      >
                        {payment.payment_method === 'direct_debit' ? '💳 Direct Debit' : '💰 Wallet'}
                      </Badge>
                    </Td>
                    <Td>
                      <VStack align="start" spacing={0}>
                        <Text fontSize="sm" color="gray.500">
                          {payment.account_number_masked || payment.account_number || '-'}
                        </Text>
                        {payment.bank_name && payment.bank_name !== 'N/A' && (
                          <Text fontSize="xs" color="gray.500">
                            {payment.bank_name}
                          </Text>
                        )}
                      </VStack>
                    </Td>
                    <Td>
                      <VStack align="start" spacing={0}>
                        <Text fontSize="sm" color="gray.600" fontFamily="mono">
                          {payment.payment_reference || '-'}
                        </Text>
                        {payment.payment_method === 'direct_debit' && payment.direct_debit_transaction_id && (
                          <Text fontSize="xs" color="gray.500" fontFamily="mono">
                            DD: {payment.direct_debit_transaction_id}
                          </Text>
                        )}
                        {payment.payment_method === 'wallet' && payment.flutterwave_transfer_id && (
                          <Text fontSize="xs" color="gray.500" fontFamily="mono">
                            Transfer: {payment.flutterwave_transfer_id}
                          </Text>
                        )}
                      </VStack>
                    </Td>
                    <Td>
                      <VStack align="start" spacing={1}>
                        <Badge 
                          colorScheme={getStatusColor(payment.payment_status)}
                          variant="subtle"
                        >
                          <HStack spacing={1}>
                            {getStatusIcon(payment.payment_status)}
                            <Text>{getStatusText(payment.payment_status)}</Text>
                          </HStack>
                        </Badge>
                        {payment.batch_count && (
                          <Text fontSize="xs" color="gray.500">
                            {payment.batch_count} batch{payment.batch_count > 1 ? 'es' : ''}
                          </Text>
                        )}
                        {payment.processed_by && (
                          <Text fontSize="xs" color="gray.500">
                            by {payment.processed_by}
                          </Text>
                        )}
                        {payment.failure_reason && (
                          <Tooltip label={payment.failure_reason}>
                            <Text fontSize="xs" color="red.500" mt={1}>
                              {payment.failure_reason.substring(0, 50)}...
                            </Text>
                          </Tooltip>
                        )}
                      </VStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </Card>
      ) : (
        <Alert status="info" borderRadius="lg">
          <AlertIcon />
          <Box>
            <AlertTitle>No Payment History</AlertTitle>
            <AlertDescription>
              {searchTerm || categoryNameFilter || paymentStatusFilter
                ? "No payments found matching your filters. Try adjusting your search criteria."
                : "No settlement payments have been processed yet. Payment history will appear here after you process settlement payments."}
            </AlertDescription>
          </Box>
        </Alert>
      )}
      
      {/* Pagination */}
      {totalPages > 1 && (
        <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
          <CardBody>
            <Flex justify="space-between" align="center">
              <Text fontSize="sm" color="gray.600">
                Page {currentPage} of {totalPages}
              </Text>
              <HStack spacing={2}>
                <IconButton
                  icon={<FiChevronLeft />}
                  aria-label="Previous page"
                  onClick={handlePreviousPage}
                  isDisabled={currentPage === 1}
                  size="sm"
                />
                <Text fontSize="sm" fontWeight="600" minW="60px" textAlign="center">
                  {currentPage}
                </Text>
                <IconButton
                  icon={<FiChevronRight />}
                  aria-label="Next page"
                  onClick={handleNextPage}
                  isDisabled={currentPage === totalPages}
                  size="sm"
                />
              </HStack>
            </Flex>
          </CardBody>
        </Card>
      )}
    </VStack>
  );
};

export default SettlementPaymentHistory;

